import React, { useState } from 'react'
import Popover from 'react-popover'
import classnames from 'classnames'
import { isMobile } from 'react-device-detect'

import AvailableDomain from 'app/components/SVGS/AvailableDomain'
import {
  scrollToActionButton,
  scrollToStoryInput,
  scrollToUserNotifications,
  scrollToPreviousMicroActivityCard,
} from 'helpers/onboarding'
import { IOnboardingChecklistItemsProps } from './interface'
import { EColors } from 'app/constants/colors'

import './styles.scss'

export const bc = 'onboarding-check-list-items'

const OnboardingChecklistItems: React.FC<IOnboardingChecklistItemsProps> = ({
  t,
  featureAvailabilityParams: { isStoriesEnabled, isSMSEnabled, isSurveyEnabled },
  checklist: {
    isFirstActionCompleted,
    isStoryShared,
    isMobileNumberAdded,
    areNotificationSettingsUpdated,
    isQuizCompleted,
  },
  triggerAddMobileNumber,
  showTypeFormPopup,
  isOnlyPreTrackSurveyEnable,
  isOnlyPostSurveyEnable,
  isProTrackInProgress,
  isProTrackStopped,
  isWorkingDay,
  activityHistoryItems,
}) => {
  const [showStoryPopover, toggleShowStoryPopover] = useState(false)
  const [showQuizPopover, toggleShowQuizPopover] = useState(false)
  const [showFirstActionPopover, toggleShowFirstActionPopover] = useState(false)

  const defaultIconProps = {
    checkcolor: EColors.LIGHT_GREY,
    circleborder: EColors.PALE_GREY,
    background: EColors.WHITE,
    width: (isMobile && '32px') || '38px',
    height: (isMobile && '32px') || '38px',
  }
  const checkIconProps = {
    background: EColors.BLUE,
    circleborder: EColors.BLUE,
    checkcolor: EColors.WHITE,
  }

  const listItemPopover = (label: string, show: boolean, toggleCb: (show: boolean) => void) => {
    return (
      <Popover
        isOpen={show}
        body={
          <div className={`${bc}__item-popover`}>
            <div>{t(label)}</div>
          </div>
        }
        preferPlace="right"
        tipSize={7}
        style={{ width: 230 }}
      >
        <div className={`${bc}__item-tooltip`} onMouseOver={() => toggleCb(true)} onMouseOut={() => toggleCb(false)}>
          ?
        </div>
      </Popover>
    )
  }

  let buttonDisabler = isQuizCompleted
  let onQuizClickCallback = () => {
    if (!isQuizCompleted) {
      showTypeFormPopup()
    }
  }

  if (isOnlyPreTrackSurveyEnable) {
    buttonDisabler = !isProTrackInProgress
    onQuizClickCallback = () => {
      if (isProTrackInProgress && !isQuizCompleted) {
        showTypeFormPopup()
      }
    }
  }

  if (isOnlyPostSurveyEnable) {
    buttonDisabler = isProTrackInProgress
    onQuizClickCallback = () => {
      if (!isProTrackInProgress && !isQuizCompleted) {
        showTypeFormPopup()
      }
    }
  }

  if (isProTrackStopped) {
    buttonDisabler = true
    onQuizClickCallback = () => {}
  }

  const isNonWorkingDayWithActivity = !isFirstActionCompleted && !isWorkingDay && activityHistoryItems.size > 0
  const isWorkingDayWithActivity = !isFirstActionCompleted && isWorkingDay && !isProTrackStopped
  const isStoryNotSharedWorkingDay = isFirstActionCompleted && !isStoryShared && isWorkingDay && !isProTrackStopped
  const isStoryNotSharedNonWorkingDay =
    isFirstActionCompleted && !isStoryShared && !isWorkingDay && !isProTrackStopped && activityHistoryItems.size > 0

  return (
    <div className={`${bc}__wrapper`}>
      <div
        className={classnames(`${bc}__link`, {
          [`${bc}__link--disabled`]: isFirstActionCompleted || isProTrackStopped,
        })}
      >
        <div
          className={classnames({
            cursor: isWorkingDayWithActivity || isNonWorkingDayWithActivity,
          })}
          onClick={() => {
            if (isWorkingDayWithActivity) {
              scrollToActionButton()
            } else if (isNonWorkingDayWithActivity) {
              scrollToPreviousMicroActivityCard()
            }
          }}
        >
          {t('Complete First Action')}
        </div>
        <div>
          <AvailableDomain
            className={`${bc}__icon`}
            {...defaultIconProps}
            {...(isFirstActionCompleted && checkIconProps)}
          />
        </div>
        {isProTrackStopped &&
          !isFirstActionCompleted &&
          listItemPopover(
            'Select new ProTrack for start your journey',
            showFirstActionPopover,
            toggleShowFirstActionPopover
          )}
      </div>
      {isStoriesEnabled && (
        <div
          className={classnames(`${bc}__link`, {
            [`${bc}__link--disabled`]: !isFirstActionCompleted || isStoryShared || isProTrackStopped,
          })}
        >
          <div
            className={classnames({
              cursor: isStoryNotSharedWorkingDay || isStoryNotSharedNonWorkingDay,
            })}
            onClick={() => {
              if (isStoryNotSharedWorkingDay) {
                scrollToStoryInput()
              } else if (isStoryNotSharedNonWorkingDay) {
                scrollToPreviousMicroActivityCard()
              }
            }}
          >
            {t('Share Story')}
          </div>
          <div>
            <AvailableDomain className={`${bc}__icon`} {...defaultIconProps} {...(isStoryShared && checkIconProps)} />
          </div>
          {!isFirstActionCompleted &&
            listItemPopover(
              'After you complete an Activity you will be asked to share a story.',
              showStoryPopover,
              toggleShowStoryPopover
            )}
          {isProTrackStopped &&
            !isStoryShared &&
            listItemPopover('Start a new Protrack to share story.', showStoryPopover, toggleShowStoryPopover)}
        </div>
      )}
      {isSMSEnabled && (
        <div className={classnames(`${bc}__link`, { [`${bc}__link--disabled`]: isMobileNumberAdded })}>
          <div
            className={classnames({
              cursor: !isMobileNumberAdded,
            })}
            onClick={() => !isMobileNumberAdded && triggerAddMobileNumber()}
          >
            {t('Add Mobile #')}
          </div>
          <div>
            <AvailableDomain
              className={`${bc}__icon`}
              {...defaultIconProps}
              {...(isMobileNumberAdded && checkIconProps)}
            />
          </div>
        </div>
      )}
      <div className={classnames(`${bc}__link`, { [`${bc}__link--disabled`]: areNotificationSettingsUpdated })}>
        <div
          className={classnames({
            cursor: !areNotificationSettingsUpdated,
          })}
          onClick={() => !areNotificationSettingsUpdated && scrollToUserNotifications()}
        >
          {t('Notifications Settings')}
        </div>
        <div>
          <AvailableDomain
            className={`${bc}__icon`}
            {...defaultIconProps}
            {...(areNotificationSettingsUpdated && checkIconProps)}
          />
        </div>
      </div>
      {isSurveyEnabled && (
        <div className={classnames(`${bc}__link`, { [`${bc}__link--disabled`]: buttonDisabler })}>
          <div
            className={classnames({
              cursor: !isQuizCompleted && !isProTrackStopped,
            })}
            onClick={onQuizClickCallback}
          >
            {t('Complete Quiz')}
          </div>
          <div>
            <AvailableDomain className={`${bc}__icon`} {...defaultIconProps} {...(isQuizCompleted && checkIconProps)} />
          </div>
          {buttonDisabler &&
            !isQuizCompleted &&
            listItemPopover(
              isOnlyPostSurveyEnable || isOnlyPreTrackSurveyEnable
                ? 'Complete this brief quiz to set a baseline for your current habits'
                : 'Select new ProTrack for start your journey',
              showQuizPopover,
              toggleShowQuizPopover
            )}
        </div>
      )}
    </div>
  )
}

export default OnboardingChecklistItems
